import {
  MarketActionCard, MarketList, MarketRadio, MarketRow,
} from '@market/react';
import type { MarketListCustomEvent, TMarketListSelectionsDidChangeEventDetail } from '@market/web-components';
import { useTranslation } from 'react-i18next';
import { withI18n } from '@squareup/service-i18n/components';
import { PercentagePicker } from '../percentage-picker/PercentagePicker';
import styles from './RepaymentLimitContent.module.css';

export enum RepaymentLimit {
  StatementBalance = 'statement-balance',
  MinimumPayment = 'minimum-payment',
  Custom = 'custom',
}

export interface RepaymentLimitContentProps {
  readonly customPercentage: number;
  readonly selectedLimit: RepaymentLimit;
  readonly onCustomPercentageChange: (value: number) => void;
  readonly onLimitChange: (limit: RepaymentLimit) => void;
}

export const RepaymentLimitContent = ({
  selectedLimit,
  onLimitChange,
  customPercentage,
  onCustomPercentageChange,
}: RepaymentLimitContentProps) => {
  const { t } = useTranslation();

  const handleSelectionChange = (
    event: MarketListCustomEvent<TMarketListSelectionsDidChangeEventDetail>,
  ) => {
    const newValue = event.detail.currentSelectionValues?.[0];
    if (newValue) {
      onLimitChange(newValue as RepaymentLimit);
    }
  };

  return (
    <div className={styles.content}>
      <p className="paragraph-30">
        {t('bankingShared.creditCard.repaymentLimit.subtitle')}
      </p>

      <MarketList
        interactive
        value={selectedLimit}
        onMarketListSelectionsDidChange={handleSelectionChange}
      >
        <MarketActionCard value={RepaymentLimit.StatementBalance}>
          <MarketRow>
            <label slot="label">{t('bankingShared.creditCard.repaymentLimit.statementBalance')}</label>
            <p className={styles.optionSubtext} slot="subtext">{t('bankingShared.creditCard.repaymentLimit.statementBalanceSubtext')}</p>
            <MarketRadio slot="control" />
          </MarketRow>
        </MarketActionCard>

        <MarketActionCard value={RepaymentLimit.Custom}>
          <MarketRow>
            <label slot="label">{t('bankingShared.creditCard.repaymentLimit.custom')}</label>
            <p className={styles.optionSubtext} slot="subtext">{t('bankingShared.creditCard.repaymentLimit.customSubtext')}</p>
            <MarketRadio slot="control" />
          </MarketRow>

          {selectedLimit === RepaymentLimit.Custom && (
            <div className={styles.percentagePickerContainer}>
              <PercentagePicker
                value={customPercentage}
                size="small"
                onChange={onCustomPercentageChange}
              />
            </div>
          )}
        </MarketActionCard>

        <MarketActionCard value={RepaymentLimit.MinimumPayment}>
          <MarketRow>
            <label slot="label">{t('bankingShared.creditCard.repaymentLimit.minimumPayment')}</label>
            <MarketRadio slot="control" />
          </MarketRow>
        </MarketActionCard>
      </MarketList>
    </div>
  );
};

export const RepaymentLimitContentI18n = withI18n(RepaymentLimitContent);
